'use client';

import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { sectionHeadingTag } from '@/components/dynamic-zone/section-heading-level';
import { components } from '@/types/strapi';
import { SectionContainer } from '../../ui/SectionContainer';

export function CoreValuesBlock({
	heading,
	sub_heading,
	items,
	backgroundColor,
	isFirst = false,
}: components['schemas']['DynamicZoneCoreValuesBlockComponent'] & {
	isFirst?: boolean;
}) {
	const SectionHeading = sectionHeadingTag(isFirst);
	const [cardHeight, setCardHeight] = useState<number | null>(null);
	const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

	// Update card heights on mount and window resize
	useEffect(() => {
		updateCardHeights();

		const handleResize = () => {
			updateCardHeights();
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const updateCardHeights = () => {
		if (cardRefs.current.length > 0) {
			const heights = cardRefs.current
				.filter(Boolean)
				.map(ref => ref?.offsetHeight || 0);
			const maxHeight = Math.max(...heights);
			if (maxHeight > 0) {
				setCardHeight(maxHeight);
			}
		}
	};

	return (
		<SectionContainer
			withBackground
			className="core-values-section mt-16"
			style={{ backgroundColor }}>
			<noscript>
				<style>{`
					.core-values-section [style*='opacity'],
					.core-values-section [style*='translateY'],
					.core-values-section [style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="container mx-auto px-4 py-8">
				<div className="mx-auto mb-8 max-w-4xl text-center">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.6 }}>
						<SectionHeading className="mb-3 text-3xl font-bold md:text-4xl">
							{heading}
						</SectionHeading>
						{/* <div className="mx-auto mb-6 h-1 w-20 rounded-full bg-primary"></div> */}
						{sub_heading && (
							<p className="mx-auto max-w-3xl text-lg text-gray-600">
								{sub_heading}
							</p>
						)}
					</motion.div>
				</div>

				<div className="grid grid-cols-1 gap-8 md:grid-cols-2">
					{items?.map((item, index) => (
						<motion.div
							key={item.title}
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.5, delay: index * 0.2 }}
							className="group h-full">
							<div
								ref={el => {
									cardRefs.current[index] = el;
								}}
								className="relative flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-8 shadow-md transition-all duration-300 hover:shadow-xl"
								style={{
									height: cardHeight
										? `${cardHeight}px`
										: 'auto',
								}}>
								{/* Subtle background gradient */}
								<div
									className={`absolute inset-0 bg-gradient-to-br ${item.bgGradient} rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100`}></div>

								{/* Decorative circle */}
								<div className="absolute right-0 top-0 -mr-10 -mt-10 h-32 w-32 rounded-full bg-gray-50 opacity-50"></div>

								<div className="relative z-10 flex h-full flex-col">
									{item.icon && (
										<div
											className={`mb-6 bg-gradient-to-br ${item.gradient} flex h-16 w-16 transform items-center justify-center rounded-lg text-white shadow-md transition-transform duration-300 group-hover:scale-110`}>
											{item.icon}
										</div>
									)}
									{item.title && (
										<h3 className="mb-4 text-2xl font-bold">
											{item.title}
										</h3>
									)}
									{item.description && (
										<div
											className="mb-6 text-base leading-relaxed text-gray-600"
											dangerouslySetInnerHTML={{
												__html: item.description,
											}}
										/>
									)}
									{item.impact && (
										<div className="mt-auto border-t border-gray-100 pt-4">
											<p
												className={`bg-gradient-to-r text-sm font-medium ${item.gradient} flex items-center bg-clip-text text-transparent`}>
												{item.impact}
											</p>
										</div>
									)}
								</div>
							</div>
						</motion.div>
					))}
				</div>
			</div>
		</SectionContainer>
	);
}
